import React, { useMemo, useState } from 'react'
import { SelectProps } from './types'
import { ExclamationCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline'

/**
 * Componente Select minimalista con diseño limpio y sutil
 */
const Select: React.FC<SelectProps> = ({
  error = false,
  errorMessage,
  disabled = false,
  readOnly = false,
  mode,
  options = [],
  placeholder,
  className = '',
  size = 'md',
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false)

  // Determinar si está en modo readonly según mode
  const isReadOnly = useMemo(() => {
    if (readOnly) return true
    if (mode === 'view') return true
    return false
  }, [readOnly, mode])

  // Clases base según tamaño - minimalista
  const sizeClasses = useMemo(() => {
    switch (size) {
      case 'sm':
        return 'px-2.5 py-1.5 text-sm'
      case 'lg':
        return 'px-3.5 py-2.5 text-base'
      default:
        return 'px-3 py-2 text-sm'
    }
  }, [size])

  // Clases para el contenedor
  const containerClasses = useMemo(() => {
    return `relative ${className}`
  }, [className])

  // Clases para el select - diseño minimalista
  const selectClasses = useMemo(() => {
    // Base minimalista: sin sombras, bordes sutiles
    const baseClasses = `w-full rounded-md border transition-colors duration-150 focus:outline-none focus:ring-1 focus:ring-offset-0 appearance-none cursor-pointer ${sizeClasses} pr-8 bg-white`
    
    const stateClasses = disabled
      ? 'bg-gray-50 text-gray-500 cursor-not-allowed border-gray-200'
      : isReadOnly
      ? 'bg-gray-50 text-gray-700 border-gray-200 cursor-not-allowed'
      : error
      ? 'border-red-300 text-gray-900 focus:ring-red-400 focus:border-red-400'
      : `border-gray-300 text-gray-900 focus:ring-blue-400 focus:border-blue-400 ${isFocused ? '' : 'hover:border-gray-400'}`
    
    return `${baseClasses} ${stateClasses}`
  }, [sizeClasses, disabled, isReadOnly, error, isFocused])

  // Clases para el chevron - minimalista
  const chevronClasses = useMemo(() => {
    if (disabled || isReadOnly) return 'h-4 w-4 text-gray-400'
    if (error) return 'h-4 w-4 text-red-400'
    return 'h-4 w-4 text-gray-400'
  }, [disabled, isReadOnly, error])

  // Handlers
  const handleFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
    setIsFocused(true)
    if (rest.onFocus) rest.onFocus(e)
  }

  const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
    setIsFocused(false)
    if (rest.onBlur) rest.onBlur(e)
  }

  // Verificar si ya existe una opción con value vacío para evitar duplicar el placeholder
  const hasEmptyOption = options.some(option => option.value === '' || option.value === null || option.value === undefined)
  
  return (
    <div className={containerClasses}>
      {/* Select minimalista */}
      <select
        disabled={disabled || isReadOnly}
        className={selectClasses}
        aria-invalid={error}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...rest}
      >
        {/* Placeholder como opción - solo si no existe ya una opción vacía */}
        {placeholder && !hasEmptyOption && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        
        {/* Opciones */}
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>

      {/* Icono de chevron - minimalista */}
      <div className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none z-10">
        <ChevronDownIcon className={chevronClasses} />
      </div>

      {/* Mensaje de error - minimalista */}
      {error && errorMessage && (
        <div className="mt-1 flex items-center gap-1">
          <ExclamationCircleIcon className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
          <span className="text-xs text-red-600">{errorMessage}</span>
        </div>
      )}
    </div>
  )
}

export default React.memo(Select)
